import React, { useState, useEffect } from 'react';
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaEye,
  FaUser,
  FaPhone,
  FaSchool,
  FaAward,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';
import { studentAPI, getImageUrl } from '../services/api';
import Modal from '../components/Modal';
import './StudentsPage.css';

function StudentsPage() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingStudent, setViewingStudent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    FirstName: '',
    LastName: '',
    DOB: '',
    Gender: 'M',
    FatherName: '',
    MotherName: '',
    SchoolName: '',
    SchoolGrade: '',
    Address: '',
    ContactNumber: '',
    EmergencyContact: '',
    PhotoPath: '',
    RegistrationDate: new Date().toISOString().split('T')[0],
    SkillLevel: 'Beginner',
    TermsAccepted: false,
  });

  useEffect(() => {
    fetchStudents();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await studentAPI.getAll();
      setStudents(response.data);
      setError('');
    } catch (err) {
      setError('Failed to fetch students: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredStudents = students.filter((student) =>
    `${student.FirstName} ${student.LastName}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const formatDateForInput = (dateVal) => {
    if (!dateVal) return '';
    if (typeof dateVal === 'string') {
      const match = dateVal.match(/^\d{4}-\d{2}-\d{2}/);
      if (match) return match[0];
    }
    try {
      const d = new Date(dateVal);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    } catch {}
    return '';
  };

  const handleOpenModal = (student = null) => {
    if (student) {
      setEditingId(student.StudentID);
      setFormData({
        ...student,
        DOB: formatDateForInput(student.DOB || student.dob),
        RegistrationDate: formatDateForInput(student.RegistrationDate || student.registrationDate),
        TermsAccepted: Boolean(student.TermsAccepted),
      });
    } else {
      setEditingId(null);
      setFormData({
        FirstName: '',
        LastName: '',
        DOB: '',
        Gender: 'M',
        FatherName: '',
        MotherName: '',
        SchoolName: '',
        SchoolGrade: '',
        Address: '',
        ContactNumber: '',
        EmergencyContact: '',
        PhotoPath: '',
        RegistrationDate: new Date().toISOString().split('T')[0],
        SkillLevel: 'Beginner',
        TermsAccepted: false,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingId(null);
  };

  const handleOpenViewModal = (student) => {
    setViewingStudent(student);
    setIsViewModalOpen(true);
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setViewingStudent(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return String(dateString);
      return d.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return String(dateString).split('T')[0];
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file' && files.length > 0) {
      // Store the file object for FormData
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Create FormData for multipart/form-data submission
      const data = new FormData();
      
      // Add all form fields to FormData
      Object.keys(formData).forEach((key) => {
        if (key === 'PhotoPath') {
          // Only add the file if it's a File object (new upload)
          if (formData[key] instanceof File) {
            data.append(key, formData[key]);
          }
        } else {
          data.append(key, formData[key]);
        }
      });

      if (editingId) {
        await studentAPI.update(editingId, data);
        setError('');
      } else {
        await studentAPI.create(data);
        setError('');
      }
      await fetchStudents();
      handleCloseModal();
    } catch (err) {
      setError('Failed to save student: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await studentAPI.delete(id);
        fetchStudents();
        setError('');
      } catch (err) {
        setError('Failed to delete student: ' + err.message);
      }
    }
  };

  const studentStats = [
    { label: 'Total Students', value: students.length },
    { label: 'Beginners', value: students.filter((item) => item.SkillLevel === 'Beginner').length },
    { label: 'Intermediate', value: students.filter((item) => item.SkillLevel === 'Intermediate').length },
    { label: 'Advanced', value: students.filter((item) => item.SkillLevel === 'Advanced').length }
  ];

  return (
    <div className="students-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">Student Management</h1>
          <button
            className="btn btn-primary"
            onClick={() => handleOpenModal()}
          >
            <FaPlus /> Add Student
          </button>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        <div className="summary-grid">
          {studentStats.map((item) => (
            <article className="summary-card" key={item.label}>
              <p>{item.label}</p>
              <h3>{item.value}</h3>
            </article>
          ))}
        </div>

        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search students by name..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        {loading ? (
          <div className="loading">Loading students...</div>
        ) : filteredStudents.length === 0 ? (
          <div className="no-data">No students found</div>
        ) : (
          <div className="table-responsive">
            <table className="responsive-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>DOB</th>
                  <th>School</th>
                  <th>Contact</th>
                  <th>Skill Level</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student) => (
                  <tr key={student.StudentID}>
                    <td data-label="Name">{student.FirstName} {student.LastName}</td>
                    <td data-label="DOB">{formatDate(student.DOB)}</td>
                    <td data-label="School">{student.SchoolName || 'N/A'}</td>
                    <td data-label="Contact">{student.ContactNumber || 'N/A'}</td>
                    <td data-label="Skill Level">{student.SkillLevel || 'Beginner'}</td>
                    <td data-label="Actions" className="action-buttons">
                      <button
                        className="btn btn-small btn-info"
                        onClick={() => handleOpenViewModal(student)}
                        title="View Student Details"
                      >
                        <FaEye /> View
                      </button>
                      <button
                        className="btn btn-small btn-primary"
                        onClick={() => handleOpenModal(student)}
                        title="Edit Student"
                      >
                        <FaEdit /> Edit
                      </button>
                      <button
                        className="btn btn-small btn-danger"
                        onClick={() => handleDelete(student.StudentID)}
                        title="Delete Student"
                      >
                        <FaTrash /> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        title={editingId ? 'Edit Student' : 'Add New Student'}
        onClose={handleCloseModal}
      >
        <form onSubmit={handleSubmit} className="student-form">
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                type="text"
                name="FirstName"
                value={formData.FirstName}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                type="text"
                name="LastName"
                value={formData.LastName}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Date of Birth</label>
              <input
                type="date"
                name="DOB"
                value={formatDateForInput(formData.DOB)}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Gender</label>
              <select
                name="Gender"
                value={formData.Gender}
                onChange={handleInputChange}
              >
                <option value="M">Male</option>
                <option value="F">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Father's Name</label>
              <input
                type="text"
                name="FatherName"
                value={formData.FatherName}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Mother's Name</label>
              <input
                type="text"
                name="MotherName"
                value={formData.MotherName}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>School Name</label>
              <input
                type="text"
                name="SchoolName"
                value={formData.SchoolName}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>School Grade</label>
              <input
                type="text"
                name="SchoolGrade"
                value={formData.SchoolGrade}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Address</label>
            <textarea
              name="Address"
              value={formData.Address}
              onChange={handleInputChange}
              rows="3"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Contact Number *</label>
              <input
                type="tel"
                name="ContactNumber"
                value={formData.ContactNumber}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="form-group">
              <label>Emergency Contact</label>
              <input
                type="tel"
                name="EmergencyContact"
                value={formData.EmergencyContact}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Skill Level</label>
              <select
                name="SkillLevel"
                value={formData.SkillLevel}
                onChange={handleInputChange}
              >
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>
            <div className="form-group">
              <label>Registration Date</label>
              <input
                type="date"
                name="RegistrationDate"
                value={formatDateForInput(formData.RegistrationDate)}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Student Photo</label>
            <input
              type="file"
              name="PhotoPath"
              onChange={handleInputChange}
              accept="image/*"
            />
            <p className="form-help">Use a square JPG or PNG image, ideally 800 x 800 px or larger, for the student card and profile preview.</p>
            {formData.PhotoPath && (
              <div className="upload-preview">
                <img 
                  src={formData.PhotoPath instanceof File ? URL.createObjectURL(formData.PhotoPath) : getImageUrl(formData.PhotoPath)} 
                  alt="Student" 
                />
              </div>
            )}
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                name="TermsAccepted"
                checked={formData.TermsAccepted}
                onChange={handleInputChange}
              />
              Terms & Conditions Accepted
            </label>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : editingId ? 'Update Student' : 'Add Student'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleCloseModal}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isViewModalOpen}
        title="Student Complete Details"
        onClose={handleCloseViewModal}
      >
        {viewingStudent && (
          <div className="student-view-modal">
            <div className="student-view-header">
              <div className="student-view-avatar">
                {viewingStudent.PhotoPath ? (
                  <img
                    src={viewingStudent.PhotoPath instanceof File ? URL.createObjectURL(viewingStudent.PhotoPath) : getImageUrl(viewingStudent.PhotoPath)}
                    alt={`${viewingStudent.FirstName} ${viewingStudent.LastName}`}
                  />
                ) : (
                  <div className="avatar-placeholder">
                    <FaUser />
                  </div>
                )}
              </div>
              <div className="student-view-title-block">
                <h3>{viewingStudent.FirstName || ''} {viewingStudent.LastName || ''}</h3>
                <div className="student-view-badges">
                  <span className="badge-chip badge-id">ID: {viewingStudent.StudentID || 'N/A'}</span>
                  <span className={`badge-chip badge-skill skill-${String(viewingStudent.SkillLevel || '').toLowerCase()}`}>
                    <FaAward /> {viewingStudent.SkillLevel || 'Beginner'}
                  </span>
                  <span className="badge-chip badge-gender">
                    {viewingStudent.Gender === 'F' ? 'Female' : viewingStudent.Gender === 'M' ? 'Male' : viewingStudent.Gender || 'Other'}
                  </span>
                </div>
              </div>
            </div>

            <div className="student-detail-grid">
              <div className="detail-section">
                <h4><FaUser /> Personal Information</h4>
                <div className="detail-row">
                  <span className="detail-label">First Name:</span>
                  <span className="detail-value">{viewingStudent.FirstName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Last Name:</span>
                  <span className="detail-value">{viewingStudent.LastName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Date of Birth:</span>
                  <span className="detail-value">{formatDate(viewingStudent.DOB)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Gender:</span>
                  <span className="detail-value">
                    {viewingStudent.Gender === 'F' ? 'Female' : viewingStudent.Gender === 'M' ? 'Male' : viewingStudent.Gender || 'Other'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Registration Date:</span>
                  <span className="detail-value">{formatDate(viewingStudent.RegistrationDate)}</span>
                </div>
              </div>

              <div className="detail-section">
                <h4><FaSchool /> Family & School</h4>
                <div className="detail-row">
                  <span className="detail-label">Father's Name:</span>
                  <span className="detail-value">{viewingStudent.FatherName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Mother's Name:</span>
                  <span className="detail-value">{viewingStudent.MotherName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">School Name:</span>
                  <span className="detail-value">{viewingStudent.SchoolName || 'N/A'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">School Grade:</span>
                  <span className="detail-value">{viewingStudent.SchoolGrade || 'N/A'}</span>
                </div>
              </div>

              <div className="detail-section full-span">
                <h4><FaPhone /> Contact & Location</h4>
                <div className="detail-row">
                  <span className="detail-label">Contact Number:</span>
                  <span className="detail-value">
                    {viewingStudent.ContactNumber ? (
                      <a href={`tel:${viewingStudent.ContactNumber}`} className="contact-link">
                        {viewingStudent.ContactNumber}
                      </a>
                    ) : 'N/A'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Emergency Contact:</span>
                  <span className="detail-value">
                    {viewingStudent.EmergencyContact ? (
                      <a href={`tel:${viewingStudent.EmergencyContact}`} className="contact-link">
                        {viewingStudent.EmergencyContact}
                      </a>
                    ) : 'N/A'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Address:</span>
                  <span className="detail-value">{viewingStudent.Address || 'N/A'}</span>
                </div>
              </div>

              <div className="detail-section full-span">
                <h4><FaAward /> Skating & Terms</h4>
                <div className="detail-row">
                  <span className="detail-label">Skill Level:</span>
                  <span className="detail-value">{viewingStudent.SkillLevel || 'Beginner'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Terms Accepted:</span>
                  <span className="detail-value">
                    {viewingStudent.TermsAccepted ? (
                      <span className="status-badge status-accepted"><FaCheckCircle /> Accepted</span>
                    ) : (
                      <span className="status-badge status-pending"><FaTimesCircle /> Not Accepted</span>
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="student-view-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  handleCloseViewModal();
                  handleOpenModal(viewingStudent);
                }}
              >
                <FaEdit /> Edit Student
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCloseViewModal}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default StudentsPage;
